import {
  Box,
  Dialog,
  DialogTitle,
  Grid,
  Typography,
  TextField as TextFieldMaterial,
  Chip,
  IconButton,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Autocomplete } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { BaseButton } from 'components/BaseButton';
import SelectField from 'components/select-field';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { TFunction } from 'i18next';
import ReactQuill from 'react-quill';
import {
  normalizeHtmlQuill,
  normalizeQuillHtml,
} from 'views/email-templates/components/notificationSelected';
import { Notifications } from 'views/email-templates/types';
import { NewMessageType, Party } from '../types';
import { emailTemplate } from 'views/email-templates/assets/emailTemplate';
import TextField from 'components/text-field';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import validations from 'utils/validations';
import RecipientsPicker from './recipients-picker';
import { buildRecipientOptions, matchesRecipient } from '../utils/recipients';

interface SendMessageProps {
	open: boolean;
	handleClose: () => void;
	t: TFunction;
	notifications: Notifications[];
	parties: Party[];
	hanldeSubmitNotification: (data: any) => void;
	isSending: boolean;
}

type RecipientFieldName = 'to' | 'cc' | 'bcc' | 'reply_to';

const PRIMARY_RECIPIENT: { name: RecipientFieldName; label: string } = { name: 'to', label: 'messages:to' };

const EXTRA_RECIPIENTS: { name: RecipientFieldName; label: string }[] = [
  { name: 'cc', label: 'messages:cc' },
  { name: 'bcc', label: 'messages:bcc' },
  { name: 'reply_to', label: 'messages:reply-to' },
];

const COARSE_POINTER = '@media (hover: none) and (pointer: coarse)';

const useStyles = makeStyles((theme: any) =>
  createStyles({
    containerChip: {
      backgroundColor: theme.palette.secondary.main,
      margin: theme.spacing(0.5),
      maxWidth: '100%',
      height: 'auto',
      minHeight: theme.spacing(3),
      '& .MuiChip-label': {
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
      },
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0,
      gap: theme.spacing(1.5),
      padding: theme.spacing(0, 1, 0, 0),
      [theme.breakpoints.up('sm')]: { padding: theme.spacing(1) },
    },
    tabsBar: {
      flexShrink: 0,
      '& .MuiTab-root': { textTransform: 'none' },
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      padding: theme.spacing(1, 2, 0),
      [theme.breakpoints.up('sm')]: { padding: theme.spacing(2, 3, 0) },
    },
    bodyStacked: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      width: '100%',
    },
    grid: {
      flex: 1,
      minHeight: 0,
      flexWrap: 'nowrap',
      gap: theme.spacing(2),
    },
    compose: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minWidth: 0,
      minHeight: 0,
      gap: theme.spacing(1.5),
      overflowY: 'auto',
    },
    previewColumn: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minWidth: 0,
      minHeight: 0,
    },
    recipientsToggle: {
      alignSelf: 'flex-start',
    },
    recipientRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(0.5),
    },
    recipientAutocomplete: {
      flex: 1,
      minWidth: 0,
    },
    pickerButton: {
      marginTop: theme.spacing(0.5),
      [COARSE_POINTER]: { minWidth: theme.spacing(5.5), minHeight: theme.spacing(5.5) },
    },
    option: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
    },
    optionName: {
      color: theme.palette.primary.dark,
      fontWeight: 500,
      overflowWrap: 'anywhere',
    },
    optionAddress: {
      color: theme.palette.text.light,
      overflowWrap: 'anywhere',
    },
    optionRoles: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(0.5),
      marginTop: theme.spacing(0.5),
    },
    roleBadge: {
      display: 'inline-block',
      padding: theme.spacing(0.25, 0.75),
      borderRadius: 10,
      backgroundColor: theme.palette.background.default,
      border: `1px solid ${theme.palette.border.line}`,
      color: theme.palette.primary.main,
      fontSize: '0.75rem',
      lineHeight: 1.4,
      whiteSpace: 'nowrap',
    },
    editorWrapper: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 220,
      '& > div': { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 },
      '& .ql-toolbar': { flexShrink: 0, whiteSpace: 'nowrap', overflowX: 'auto' },
      '& .ql-container': { flex: 1, minHeight: 0, fontSize: '1rem' },
      '& .ql-editor': { height: '100%' },
    },
    preview: {
      flex: 1,
      minHeight: 0,
      width: '100%',
      border: 'unset',
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.spacing(1),
      flexShrink: 0,
      paddingTop: theme.spacing(2),
      paddingBottom: `calc(${theme.spacing(2)} + env(safe-area-inset-bottom))`,
      [COARSE_POINTER]: {
        '& .MuiButton-root': { minHeight: theme.spacing(5.5) },
      },
    },
    input: {
      '&:focus': { backgroundColor: theme.palette.background.default },
      '& .MuiFilledInput-root': {
        textTransform: 'none',
        fontSize: '0.875rem',
        color: 'black',
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
        backgroundColor: theme.palette.background.default,
      },
      '& .Mui-disabled': { cursor: 'not-allowed' },
    },
  })
);

export const SendMessage: FC<SendMessageProps> = ({
  open,
  handleClose,
  t,
  notifications,
  parties,
  hanldeSubmitNotification,
  isSending,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isStacked = useMediaQuery(theme.breakpoints.down('lg'));

  const schema = z.object({
    subject: validations.requiredString(t('validations:required')),
    to: z.array(z.string()).min(1, t('validations:required')),
    cc: z.array(z.string()).optional(),
    bcc: z.array(z.string()).optional(),
    reply_to: z.array(z.string()).optional(),
  });

  const [valueQuill, setValueQuill] = useState('');
  const [template, setTemplate] = useState('');
  const [iframeReady, setIframeReady] = useState(false);
  const [stackedTab, setStackedTab] = useState<number>(0);
  const [showExtraRecipients, setShowExtraRecipients] = useState(false);
  const [pickerTarget, setPickerTarget] = useState<RecipientFieldName | null>(null);

  const quillRef = useRef<ReactQuill>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const templateSubjectRef = useRef('');

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { isValid },
  } = useForm<NewMessageType>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { subject: '', to: [], cc: [], bcc: [], reply_to: [] },
  });

  // Reset readiness when the iframe node mounts so the preview re-renders after a remount.
  const attachPreview = useCallback((node: HTMLIFrameElement | null) => {
    iframeRef.current = node;
    if (node) setIframeReady(false);
  }, [setIframeReady]);

  const handleIframeLoad = () => {
    setIframeReady(true);
  };

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframeReady || !iframe || !iframe.contentDocument) return;

    const doc = iframe.contentDocument;
    const finalHtml = emailTemplate.replace(
      'REPLACE_THIS_WITH_CONTENT',
      normalizeQuillHtml(valueQuill)
    );
    doc.open();
    doc.write(finalHtml);
    doc.close();
  }, [iframeReady, valueQuill]);

  const handleTemplateChange = (templateId: string) => {
    setTemplate(templateId);
    const notification = notifications.find((notif) => notif._id === templateId);
    setValueQuill(normalizeHtmlQuill(notification?.html ?? ''));
    const editor = quillRef.current?.getEditor();
    const currentSubject = getValues('subject');
    if (!currentSubject || currentSubject === templateSubjectRef.current) {
      templateSubjectRef.current = notification?.subject ?? '';
      setValue('subject', templateSubjectRef.current);
    }
    if (editor) {
      editor.setSelection(0, 0);
    }
  };

  const emailOptions = useMemo(() => buildRecipientOptions(parties), [parties]);

  const pickerTargetLabel = pickerTarget
    ? t([PRIMARY_RECIPIENT, ...EXTRA_RECIPIENTS].find((field) => field.name === pickerTarget)?.label ?? '')
    : '';

  const handlePickerConfirm = (values: string[]) => {
    if (!pickerTarget) return;
    const current = getValues(pickerTarget) ?? [];
    const merged = Array.from(new Set([...current, ...values]));
    setValue(pickerTarget, merged, { shouldValidate: true });
    setPickerTarget(null);
  };

  const renderRecipientField = ({ name, label }: { name: RecipientFieldName; label: string }) => (
    <Controller
      key={name}
      name={name}
      control={control}
      render={({ field }) => (
        <Box className={classes.recipientRow}>
          <Autocomplete
            multiple
            size="small"
            className={classes.recipientAutocomplete}
            disabled={isSending}
            options={emailOptions}
            getOptionLabel={(option) => option.value}
            filterOptions={(options, { inputValue }) => options.filter((option) => matchesRecipient(option, inputValue))}
            groupBy={(option) => option.roles[0] ?? ''}
            onChange={(_, newValues) => field.onChange(newValues.map((val) => val.value))}
            renderOption={({ key, ...props }, option) => (
              <li key={key} {...props}>
                <Box className={classes.option}>
                  <Typography variant="body2" className={classes.optionName}>{option.name}</Typography>
                  <Typography variant="caption" className={classes.optionAddress}>
                    {option.address}
                    {option.emailKind ? ` · ${option.emailKind}` : ''}
                  </Typography>
                  {option.roles.length > 0 && (
                  <Box className={classes.optionRoles}>
                    {option.roles.map((role) => (
                      <span key={role} className={classes.roleBadge}>{role}</span>
                    ))}
                  </Box>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextFieldMaterial
                {...params}
                size="small"
                label={t(label)}
                variant="filled"
                className={classes.input}
              />
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  label={option.value}
                  title={option.roles.join(', ')}
                  {...getTagProps({ index })}
                  className={classes.containerChip}
                />
              ))}
            value={emailOptions.filter((opt) => (field.value ?? []).includes(opt.value))}
          />
          <IconButton
            className={classes.pickerButton}
            aria-label={t('messages:select-recipients')}
            disabled={isSending}
            onClick={() => setPickerTarget(name)}
            size="large"
          >
            <FontAwesomeIcon icon="address-book" />
          </IconButton>
        </Box>
      )}
    />
  );

  const composeFields = (
    <>
      <SelectField
        emptyValue
        size="small"
        disabled={isSending}
        value={template}
        data={notifications}
        dataKey="subject"
        dataValue="_id"
        dataText="subject"
        label={t('messages:template')}
        handleChange={(e) => {
          handleTemplateChange(e.target.value);
        }}
      />
      <Controller
        name="subject"
        control={control}
        render={({ field: { ref, ...field } }) => (
          <TextField
            {...field}
            value={field.value}
            inputRef={ref}
            size="small"
            variant="filled"
            label={t('messages:subject')}
            disabled={isSending}
          />
        )}
      />

      {renderRecipientField(PRIMARY_RECIPIENT)}

      <Box className={classes.recipientsToggle}>
        <BaseButton
          buttonVariant="text"
          buttonColor="primary"
          size="small"
          icon={showExtraRecipients ? 'minus' : 'plus'}
          text={`${t('messages:cc')} / ${t('messages:bcc')} / ${t('messages:reply-to')}`}
          onClick={() => setShowExtraRecipients((current) => !current)}
        />
      </Box>

      {showExtraRecipients && EXTRA_RECIPIENTS.map(renderRecipientField)}

      <Box className={classes.editorWrapper}>
        <ReactQuill
          theme="snow"
          ref={quillRef}
          readOnly={isSending}
          value={valueQuill}
          onChange={setValueQuill}
          modules={{
					  toolbar: [
					    [{ size: [] }, { align: [] }],
					    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
					    [{ color: [] }, { background: [] }],
					    [{ list: 'ordered' }, { list: 'bullet' }],
					    ['link'],
					    ['clean'],
					  ],
          }}
          formats={[
					  'size',
					  'align',
					  'bold',
					  'italic',
					  'underline',
					  'strike',
					  'blockquote',
					  'color',
					  'background',
					  'list',
					  'bullet',
					  'link',
					  'clean',
          ]}
        />
      </Box>
    </>
  );

  const previewContent = (
    <iframe
      ref={attachPreview}
      title={t('messages:preview')}
      srcDoc="<!DOCTYPE html><html><head></head><body></body></html>"
      onLoad={handleIframeLoad}
      className={classes.preview}
    />
  );

  const actions = (
    <>
      <BaseButton
        buttonVariant="outlined"
        buttonColor="inherit"
        icon="times"
        text={t('common:cancel')}
        disabled={isSending}
        onClick={() => handleClose()}
      />
      <BaseButton
        type="submit"
        icon="check"
        text={t('messages:send')}
        isLoading={isSending}
        disabled={!isValid || isSending}
      />
    </>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={() => !isSending && handleClose()}
        disableEscapeKeyDown={isSending}
        fullWidth
        maxWidth="xl"
        fullScreen={fullScreen}
        PaperProps={{
				  style: { height: "100%" }
        }}
      >
        <Box className={classes.header}>
          <DialogTitle><Typography color="primary" variant="h5" component="h2">{t('messages:send-new')}</Typography></DialogTitle>

          <IconButton
            aria-label={t('common:cancel')}
            disabled={isSending}
            onClick={() => handleClose()}
            size="large"
          >
            <FontAwesomeIcon icon="times" />
          </IconButton>
        </Box>

        {isStacked && (
        <Box className={classes.tabsBar} borderBottom={1} borderColor="divider">
          <Tabs
            value={stackedTab}
            onChange={(_, value: number) => setStackedTab(value)}
            variant="fullWidth"
          >
            <Tab label={<Typography color="primary">{t('messages:compose')}</Typography>} value={0} />
            <Tab label={<Typography color="primary">{t('messages:preview')}</Typography>} value={1} />
          </Tabs>
        </Box>
        )}

        <form
          className={classes.form}
          onSubmit={handleSubmit((data) => !isSending && hanldeSubmitNotification({
					  ...data,
					  to: data.to.join(', '),
					  cc: data.cc.join(', '),
					  bcc: data.bcc.join(', '),
					  reply_to: data.reply_to.join(', '),
					  content: normalizeQuillHtml(valueQuill),
          }))}
        >
          {isStacked ? (
            <Box className={classes.bodyStacked}>
              {/* Both panels stay mounted (only hidden) so the editor and preview are preserved. */}
              <Box className={classes.compose} style={{ display: stackedTab === 0 ? 'flex' : 'none' }}>
                {composeFields}
              </Box>
              <Box className={classes.previewColumn} style={{ display: stackedTab === 1 ? 'flex' : 'none' }}>
                {previewContent}
              </Box>
            </Box>
          ) : (
            <Grid container direction="row" className={classes.grid}>
              <Grid item xs={12} md={6} className={classes.compose}>
                {composeFields}
              </Grid>
              <Grid item xs={12} md={6} className={classes.previewColumn}>
                {previewContent}
              </Grid>
            </Grid>
          )}
          <Box className={classes.footer}>
            {actions}
          </Box>
        </form>
      </Dialog>
      <RecipientsPicker
        open={pickerTarget !== null}
        onClose={() => setPickerTarget(null)}
        onConfirm={handlePickerConfirm}
        parties={parties}
        targetLabel={pickerTargetLabel}
        t={t}
      />
    </>
  );
};

export default SendMessage;
